import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  setDoc,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { ItemUI } from "../types/item";
import { User } from "../types/user";

// data model definitions

// Persisted item: `splits` is the denominator (how many portions the item is
// divided into, owner-set), `shares` maps a roster user's id to how many of
// those portions they took. A person pays price * (shares[id] / splits).
export interface PersistedItem {
  id: string;
  name: string;
  price: number;
  splits: number;
  shares: Record<string, number>;
}

// Pre-splits items stored assignments as a flat id list (equal headcount split).
type LegacyItem = { userIds?: string[] };

export interface Receipt {
  id: string;
  ownerId: string;
  title: string;
  tax: number;
  tip: number;
  pictureUrl: string;
  users: User[];
  items: PersistedItem[];
  paidUserId: string | null;
  // (older receipts also carry `claims` / `presence` from the removed
  // per-mate locking — ignored)
  updatedAt: number;
}

export const defaultReceipt: Receipt = {
  id: "",
  ownerId: "",
  title: "CheckMates",
  tax: 0,
  tip: 0,
  pictureUrl: "",
  users: [],
  items: [],
  paidUserId: null,
  updatedAt: 0,
};

// The working (in-memory) shape the UI holds, before persisting.
export interface ReceiptDraft {
  title: string;
  tax: number;
  tip: number;
  pictureUrl: string;
  users: User[];
  items: ItemUI[];
  paidUserId: string | null;
}

// mappers between the working shape and the persisted shape

// Firestore rejects `undefined`, so drop empty venmo instead of writing it.
function cleanUser(u: User): User {
  const name = u.name.trim();
  const venmo = u.venmo?.trim();
  return venmo ? { id: u.id, name, venmo } : { id: u.id, name };
}

function toPersistedFields(draft: ReceiptDraft) {
  return {
    title: draft.title,
    tax: draft.tax,
    tip: draft.tip,
    pictureUrl: draft.pictureUrl ?? "",
    paidUserId: draft.paidUserId,
    users: draft.users.map(cleanUser),
    items: draft.items.map((it) => ({
      id: it.id,
      name: it.name,
      price: it.price,
      splits: it.splits,
      shares: it.shares,
    })),
    updatedAt: Date.now(),
  };
}

// Rehydrate a persisted receipt back into the working shape. Legacy items (a
// flat `userIds` list, no splits/shares) map to splits = headcount, 1 share each.
export function fromPersisted(receipt: Receipt): ReceiptDraft {
  return {
    title: receipt.title,
    tax: receipt.tax,
    tip: receipt.tip,
    pictureUrl: receipt.pictureUrl,
    users: receipt.users,
    paidUserId: receipt.paidUserId,
    items: receipt.items.map(itemFromPersisted),
  };
}

// One persisted item -> working item, tolerating the pre-splits `userIds` shape.
function itemFromPersisted(it: PersistedItem): ItemUI {
  const legacy = (it as PersistedItem & LegacyItem).userIds;
  if (it.splits === undefined && legacy) {
    const shares: Record<string, number> = {};
    for (const uid of legacy) shares[uid] = 1;
    return {
      id: it.id,
      name: it.name,
      price: it.price,
      splits: legacy.length || 1,
      shares,
    };
  }
  return {
    id: it.id,
    name: it.name,
    price: it.price,
    splits: it.splits ?? 1,
    shares: it.shares ?? {},
  };
}

// firestore

const receiptCollection = collection(db, "receipts");

export async function createReceipt(
  ownerId: string,
  draft: ReceiptDraft
): Promise<string> {
  const docRef = await addDoc(receiptCollection, {
    ownerId,
    ...toPersistedFields(draft),
  });
  return docRef.id;
}

export async function getReceipt(id: string): Promise<Receipt | null> {
  const snap = await getDoc(doc(db, "receipts", id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Receipt, "id">) };
}

export function subscribeReceipt(
  id: string,
  cb: (receipt: Receipt | null) => void
): Unsubscribe {
  return onSnapshot(doc(db, "receipts", id), (snap) => {
    cb(
      snap.exists()
        ? { id: snap.id, ...(snap.data() as Omit<Receipt, "id">) }
        : null
    );
  });
}

export function saveReceipt(
  id: string,
  fields: Partial<Omit<Receipt, "id">>
): Promise<void> {
  return setDoc(doc(db, "receipts", id), fields, { merge: true });
}

// Persist host content edits (roster, tax, tip, title, payer) without touching
// items, so live assignments aren't clobbered.
export function saveReceiptContent(
  receiptId: string,
  content: {
    title: string;
    tax: number;
    tip: number;
    users: User[];
    paidUserId: string | null;
  }
): Promise<void> {
  return setDoc(
    doc(db, "receipts", receiptId),
    {
      title: content.title,
      tax: content.tax,
      tip: content.tip,
      users: content.users.map(cleanUser),
      paidUserId: content.paidUserId,
      updatedAt: Date.now(),
    },
    { merge: true }
  );
}

// Update one roster user's venmo on the shared receipt (a guest setting the
// handle of the mate they are). Transaction so concurrent writes don't clobber.
export async function saveUserVenmo(
  receiptId: string,
  userId: string,
  venmo: string
): Promise<void> {
  const ref = doc(db, "receipts", receiptId);
  const v = venmo.trim();
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const users = (snap.data()?.users as User[]) ?? [];
    const next = users.map((u) => {
      if (u.id !== userId) return u;
      if (v) return { ...u, venmo: v };
      const { venmo: _omit, ...rest } = u; // remove the key when cleared
      return rest as User;
    });
    tx.update(ref, { users: next });
  });
}

// Set how many shares a user takes of an item on the shared receipt (0 removes
// them) — an absolute value, so rapid edits can safely coalesce into the last
// one. Capped at what's free (their own + any open portions), so it never
// over-claims when two people go for the last portion at once. A transaction
// keeps concurrent edits from clobbering each other.
export async function setItemShare(
  receiptId: string,
  itemId: string,
  userId: string,
  shares: number
): Promise<void> {
  const ref = doc(db, "receipts", receiptId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const items = (snap.data()?.items as PersistedItem[]) ?? [];
    const next = items.map((it) => {
      if (it.id !== itemId) return it;
      const nextShares = { ...(it.shares ?? {}) };
      const others = Object.entries(nextShares)
        .filter(([u]) => u !== userId)
        .reduce((sum, [, n]) => sum + n, 0);
      const capped = Math.min(shares, Math.max(0, it.splits - others));
      if (capped > 0) nextShares[userId] = capped;
      else delete nextShares[userId];
      return { ...it, shares: nextShares };
    });
    tx.update(ref, { items: next });
  });
}

// Set an item's split count (denominator) on the shared receipt. Any share that
// now exceeds the new denominator is capped to it so we never over-collect.
export async function setItemSplits(
  receiptId: string,
  itemId: string,
  splits: number
): Promise<void> {
  const s = Math.max(1, Math.floor(splits));
  const ref = doc(db, "receipts", receiptId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const items = (snap.data()?.items as PersistedItem[]) ?? [];
    const next = items.map((it) => {
      if (it.id !== itemId) return it;
      const nextShares: Record<string, number> = {};
      for (const [u, n] of Object.entries(it.shares ?? {})) {
        const capped = Math.min(n, s);
        if (capped > 0) nextShares[u] = capped;
      }
      return { ...it, splits: s, shares: nextShares };
    });
    tx.update(ref, { items: next });
  });
}

// Set the split count on several items at once, in a single transaction (used
// by the bulk editor). Shares exceeding the new denominator are capped.
export async function setItemsSplits(
  receiptId: string,
  itemIds: string[],
  splits: number
): Promise<void> {
  const s = Math.max(1, Math.floor(splits));
  const targets = new Set(itemIds);
  const ref = doc(db, "receipts", receiptId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const items = (snap.data()?.items as PersistedItem[]) ?? [];
    const next = items.map((it) => {
      if (!targets.has(it.id)) return it;
      const nextShares: Record<string, number> = {};
      for (const [u, n] of Object.entries(it.shares ?? {})) {
        const capped = Math.min(n, s);
        if (capped > 0) nextShares[u] = capped;
      }
      return { ...it, splits: s, shares: nextShares };
    });
    tx.update(ref, { items: next });
  });
}

// Give a user every open portion on the receipt ("Claim all"): on each item,
// they end up holding whatever others don't. Others' portions and the split
// counts are untouched. One transaction, recomputed from server state, so it
// never over-claims against someone else's concurrent claim.
export async function claimAllOpen(
  receiptId: string,
  userId: string
): Promise<void> {
  const ref = doc(db, "receipts", receiptId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const items = (snap.data()?.items as PersistedItem[]) ?? [];
    const next = items.map((it) => {
      const shares = { ...(it.shares ?? {}) };
      const others = Object.entries(shares)
        .filter(([u]) => u !== userId)
        .reduce((sum, [, n]) => sum + n, 0);
      const available = it.splits - others;
      if (available <= (shares[userId] ?? 0)) return it; // nothing open here
      shares[userId] = available;
      return { ...it, shares };
    });
    tx.update(ref, { items: next });
  });
}

// Remove all of a user's portions from every item ("Unclaim all"); they go
// back to open. Split counts are untouched.
export async function unclaimAll(
  receiptId: string,
  userId: string
): Promise<void> {
  const ref = doc(db, "receipts", receiptId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const items = (snap.data()?.items as PersistedItem[]) ?? [];
    const next = items.map((it) => {
      if (!it.shares || !(userId in it.shares)) return it;
      const { [userId]: _mine, ...rest } = it.shares;
      return { ...it, shares: rest };
    });
    tx.update(ref, { items: next });
  });
}

// Remove several items from the shared receipt at once (bulk delete).
export async function deleteItems(
  receiptId: string,
  itemIds: string[]
): Promise<void> {
  const targets = new Set(itemIds);
  const ref = doc(db, "receipts", receiptId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const items = (snap.data()?.items as PersistedItem[]) ?? [];
    tx.update(ref, { items: items.filter((it) => !targets.has(it.id)) });
  });
}

// Append a brand-new roster user to the shared receipt (a guest introducing
// themselves). Transactional so concurrent joins don't clobber the roster.
export async function addRosterUser(
  receiptId: string,
  user: User
): Promise<void> {
  const ref = doc(db, "receipts", receiptId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const users = (snap.data()?.users as User[]) ?? [];
    if (users.some((u) => u.id === user.id)) return;
    tx.update(ref, { users: [...users, cleanUser(user)] });
  });
}
